import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService, type AdminStats, type AdminUser } from '@/services/adminService';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useAuth } from '@/hooks/useAuth';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { handleError } = useAdminApi();
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [statsData, usersData] = await Promise.all([
          adminService.getStats(),
          adminService.getUsers(),
        ]);
        if (cancelled) return;
        setStats(statsData);
        // 최근 가입 신청자 — joinedAt 기준 정렬, 최대 5명
        const sorted = [...usersData]
          .sort((a, b) => new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime())
          .slice(0, 5);
        setRecentUsers(sorted);
      } catch (err) {
        if (!handleError(err) && !cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [handleError]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">불러오는 중...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: '전체 구성원',
      value: stats?.totalMembers ?? 0,
      icon: 'ri-group-line',
      color: 'bg-accent-50 text-accent-600',
    },
    {
      label: '승인 대기',
      value: stats?.pendingCount ?? 0,
      icon: 'ri-user-received-line',
      color: 'bg-amber-50 text-amber-600',
    },
    {
      label: '활성 사용자',
      value: stats?.activeCount ?? 0,
      icon: 'ri-user-heart-line',
      color: 'bg-emerald-50 text-emerald-600',
    },
    {
      label: '관리자',
      value: stats?.adminCount ?? 0,
      icon: 'ri-shield-star-line',
      color: 'bg-rose-50 text-rose-600',
    },
  ];

  const getStatusLabel = (status: string) => {
    return status === 'active' ? '활성' : '대기 중';
  };

  const getStatusClass = (status: string) => {
    return status === 'active'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="px-4 pt-12 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-accent-500 font-semibold tracking-wide uppercase">관리자</span>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mt-1 leading-tight">
              대시보드
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              가족 구성원을 관리하고 승인을 처리해요
            </p>
          </div>
          <Link
            to="/"
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary-100 text-sm font-medium text-foreground-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            홈으로
          </Link>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-3">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-2xl p-4 flex flex-col gap-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.color}`}>
                <i className={`${card.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-950">{card.value}</p>
                <p className="text-xs text-foreground-500 mt-0.5">{card.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Actions */}
      <section className="px-4 pb-4">
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/admin/pending"
            className="bg-white rounded-2xl p-4 flex items-center gap-3 hover:bg-secondary-50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <i className="ri-user-received-line text-lg text-amber-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">승인 관리</p>
              <p className="text-xs text-foreground-500">
                {stats?.pendingCount ?? 0}명 대기 중
              </p>
            </div>
          </Link>
          <Link
            to="/admin/users"
            className="bg-white rounded-2xl p-4 flex items-center gap-3 hover:bg-secondary-50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <i className="ri-group-line text-lg text-emerald-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">구성원 관리</p>
              <p className="text-xs text-foreground-500">
                {stats?.totalMembers ?? 0}명 전체
              </p>
            </div>
          </Link>
          {user?.isOwner && (
            <Link
              to="/admin/families"
              className="bg-white rounded-2xl p-4 flex items-center gap-3 hover:bg-secondary-50 transition-colors cursor-pointer group col-span-2"
            >
              <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                <i className="ri-community-line text-lg text-violet-600"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">가족 공간 관리</p>
                <p className="text-xs text-foreground-500">최대 3가족을 독립적으로 생성·초대</p>
              </div>
            </Link>
          )}
          <Link
            to="/admin/family-members"
            className="bg-white rounded-2xl p-4 flex items-center gap-3 hover:bg-secondary-50 transition-colors cursor-pointer group col-span-2"
          >
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
              <i className="ri-group-line text-lg text-violet-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">가족 카드 관리</p>
              <p className="text-xs text-foreground-500">이름·관계·생일·사진 추가 및 수정</p>
            </div>
          </Link>
          <Link
            to="/admin/home-settings"
            className="bg-white rounded-2xl p-4 flex items-center gap-3 hover:bg-secondary-50 transition-colors cursor-pointer group col-span-2"
          >
            <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center">
              <i className="ri-layout-4-line text-lg text-accent-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">홈 화면 꾸미기</p>
              <p className="text-xs text-foreground-500">대표 이미지와 글씨 변경</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Recent Signups */}
      <section className="px-4 pb-8">
        <h2 className="text-sm font-bold text-foreground-900 mb-3">최근 가입 신청자</h2>
        <div className="bg-white rounded-2xl overflow-hidden">
          {recentUsers.length === 0 ? (
            <div className="p-6 text-center text-sm text-foreground-400">
              최근 가입 신청자가 없습니다
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {recentUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-foreground-600">
                      {u.name.charAt(0)}
                    </span>
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground-900 truncate">{u.name}</p>
                      {u.role === 'admin' && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-accent-100 text-[10px] font-semibold text-accent-600">
                          관리자
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-foreground-400 truncate">{u.email}</p>
                  </div>
                  {/* Status + Date */}
                  <div className="text-right flex-shrink-0">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusClass(u.status)}`}>
                      {getStatusLabel(u.status)}
                    </span>
                    <p className="text-[10px] text-foreground-400 mt-1">{u.joinedAt}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer spacing */}
      <div className="py-4"></div>
    </div>
  );
}
